<template>
  <gs-named-modal class="gs-refresh-controls"
    name="fix-negative-balance-modal"
    :title="$t('frontend_affiliate_index_fix_negative_balance')"
    width="600px"
    bg-type="regular"
  >
    <div v-if="selectedAffiliate">
      <div class="p-4">
        <!-- Show current negative balance info -->
        <div class="mb-6 p-4 bg-red-50 border border-red-200 rounded">
          <div class="text-sm font-semibold text-red-700 mb-2">
            {{ $t('frontend_affiliate_index_current_negative_balance') }}
          </div>
          <div class="flex justify-between">
            <span class="text-grey-700">{{ negativeFunnel ? negativeFunnel.name : 'Current Funnel' }}:</span>
            <span class="font-bold text-red-600">{{ negativeAmount }}</span>
          </div>
        </div>

        <!-- Explanation -->
        <div class="mb-6 text-sm text-grey-600">
          {{ $t('frontend_affiliate_index_fix_negative_explanation') }}
        </div>

        <!-- Funnel selector for positive balance -->
        <div class="mb-6">
          <label class="block text-sm font-semibold text-grey-700 mb-2">
            {{ $t('frontend_affiliate_index_select_source_funnel') }}
          </label>
          <select
            v-model="selectedSourceFunnel"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option :value="null" disabled>{{ $t('frontend_affiliate_index_select_funnel_with_positive_balance') }}</option>
            <option 
              v-for="funnel in availableFunnelsWithPositiveBalance" 
              :key="funnel.value"
              :value="funnel.value"
            >
              {{ funnel.label }}
            </option>
          </select>
          <div v-if="selectedSourceFunnelDetails" class="mt-2 p-2 bg-green-50 border border-green-200 rounded">
            <div class="text-sm">
              <span class="text-grey-600">{{ $t('frontend_affiliate_index_available_balance') }}:</span>
              <span class="font-bold text-green-600 ml-2">{{ selectedSourceFunnelDetails.human_readable_processed_without_advances }}</span>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="flex justify-end">
          <gs-button
            variant="outline"
            size="md"
            class="mr-3"
            @click="closeModal()"
          >
            {{ $t('frontend_affiliate_index_cancel') }}
          </gs-button>
          <gs-button
            size="md"
            variant="primary"
            :disabled="!canProceed"
            @click="processFixNegativeBalance()"
          >
            {{ $t('frontend_affiliate_index_fix_negative_balance_button') }}
          </gs-button>
        </div>
      </div>
    </div>
  </gs-named-modal>
</template>
<style scoped>
@media (max-width: 640px) {
  .flex.flex-row {
    min-width: 0;
    flex-wrap: wrap;
  }

  .flex.flex-row > [class~="w-1/3"],
  .flex.flex-row > [class~="w-2/3"] {
    width: 100%;
  }
}
</style>
<script>
import { mapState } from 'vuex';
import axios from 'axios';
import AffiliatePayment from '@/models/AffiliatePayment';
import Notify from '@groovepages/gd-ui-kit/notify';

export default {
  name: 'FixNegativeBalanceModal',
  props: {
    selectedAffiliate: {
      type: Object,
      default: null
    },
    negativeFunnel: {
      type: Object,
      default: null
    },
    selectedFunnel: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      selectedSourceFunnel: null,
      funnelsWithPositiveBalance: []
    };
  },
  computed: {
    ...mapState('auth', ['user']),

    negativeAmount() {
      if (!this.negativeFunnel) return '$0.00';
      return this.negativeFunnel.processed_without_advances < 0
        ? this.negativeFunnel.human_readable_processed_without_advances
        : this.selectedAffiliate.human_readable_processed_without_advances;
    },

    availableFunnelsWithPositiveBalance() {
      return this.funnelsWithPositiveBalance.map(f => ({
        value: f.id,
        label: `${f.name} (${f.human_readable_processed_without_advances})`
      }));
    },

    selectedSourceFunnelDetails() {
      if (!this.selectedSourceFunnel) return null;
      return this.funnelsWithPositiveBalance.find(f => f.id === this.selectedSourceFunnel);
    },

    canProceed() {
      return this.selectedSourceFunnel && this.selectedSourceFunnelDetails &&
             this.selectedSourceFunnelDetails.processed_without_advances > 0;
    },

    absoluteNegativeAmount() {
      const amount = this.negativeFunnel
        ? this.negativeFunnel.processed_without_advances
        : this.selectedAffiliate.processed_without_advances;
      return Math.abs(amount);
    }
  },
  watch: {
    selectedAffiliate(newVal) {
      if (newVal) {
        this.selectedSourceFunnel = null; // Only reset the selection
        this.loadFunnelsWithPositiveBalance();
      }
    }
  },
  async mounted() {
    if (this.selectedAffiliate) {
      this.selectedSourceFunnel = null; // Only reset the selection on mount
      await this.loadFunnelsWithPositiveBalance();
    }
  },
  methods: {
    async loadFunnelsWithPositiveBalance() {
      if (!this.selectedAffiliate) return;
      
      try {
        this.$loader.start('loadFunnelsWithPositiveBalance');

        // Get all funnels for this affiliate using the correct endpoint
        const { data } = await axios.get(`affiliates/${this.selectedAffiliate.id}/funnels`);

        // Filter funnels with positive balance
        if (data && data.data) {
          this.funnelsWithPositiveBalance = data.data.filter(f =>
            f.processed_without_advances > 0
          );
          console.log('Funnels with positive balance:', this.funnelsWithPositiveBalance);
        }

        this.$loader.end('loadFunnelsWithPositiveBalance');
      } catch (error) {
        console.error('Error loading funnels:', error);
        this.$loader.end('loadFunnelsWithPositiveBalance');
      }
    },

    async processFixNegativeBalance() {
      if (!this.canProceed) return;

      try {
        this.$loader.start('processFixNegativeBalance');

        const amountToZeroOut = this.absoluteNegativeAmount;
        const targetFunnelId = this.negativeFunnel ? this.negativeFunnel.id : this.selectedFunnel;

        // Get funnel names
        const sourceFunnelDetails = this.selectedSourceFunnelDetails;
        const targetFunnelName = this.negativeFunnel ? this.negativeFunnel.name : 'Current Funnel';

        // Create a clawback on the source funnel (positive balance)
        const clawbackPayment = new AffiliatePayment({
          affiliate_id: this.selectedAffiliate.id,
          funnel: this.selectedSourceFunnel,
          amount: amountToZeroOut / 100, // Convert cents to dollars
          payment_gateway: 0,
          note: `Over payment on ${targetFunnelName}`,
          transaction_id: `Over payment on ${targetFunnelName}`,
          type: 3 // Clawback
        });
        await clawbackPayment.save();

        // Create a negative advance on the target funnel (negative balance)
        const advancePayment = new AffiliatePayment({
          affiliate_id: this.selectedAffiliate.id,
          funnel: targetFunnelId,
          amount: -(amountToZeroOut / 100), // Negative amount to offset
          payment_gateway: 0,
          note: `Over payment adjusted with ${sourceFunnelDetails.name} balance`,
          transaction_id: `Over payment adjusted with ${sourceFunnelDetails.name} balance`,
          type: 2 // Commission advance
        });
        await advancePayment.save();

        // Refresh affiliate data
        this.$emit('refreshAffiliate');

        this.$loader.end('processFixNegativeBalance');
        this.resetModal();
        this.$root.$emit('modal-close', 'fix-negative-balance-modal');

        // Show success message
        Notify({
          type: 'success',
          title: this.$t('frontend_affiliate_index_fix_negative_success'),
          text: this.$t('frontend_affiliate_index_fix_negative_success_message')
        });
      } catch (error) {
        console.error('Error processing fix negative balance:', error);
        this.$loader.end('processFixNegativeBalance');
        Notify({
          type: 'error',
          title: this.$t('frontend_affiliate_index_fix_negative_error'),
          text: error.message || this.$t('frontend_affiliate_index_fix_negative_error_message')
        });
      }
    },

    resetModal() {
      this.selectedSourceFunnel = null;
      // Don't reset funnelsWithPositiveBalance here - let loadFunnelsWithPositiveBalance handle it
    },

    closeModal() {
      this.$root.$emit('modal-close', 'fix-negative-balance-modal');
      this.resetModal();
    }
  }
};
</script>
